import { colors } from '@kmns-internal/furikake'
import { InfoOutline, OpenInNew } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Drawer,
  Stack,
  TablePagination,
  Typography,
} from '@mui/material'
import type {
  GetUsersRequest,
  SortOrderEnum,
  UserSortColumnEnum,
} from '@ulysses-inc/harami_api_client'
import { FeatureNameEnum } from '@ulysses-inc/harami_api_client'
import { type FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { withRouter } from 'react-router-dom'
import { Header } from 'src/components/header/Header'
import { HeaderTitle } from 'src/components/header/HeaderTitle'
import { getKaminashiIDBasePath } from 'src/config'
import { getUniqueFlattenNodes } from 'src/exShared/util/place/getUniqueFlattenNodes'
import { flattenNodes } from 'src/exShared/util/place/placeNode'
import { useFeature } from 'src/features/featureFlags/useFeatureFlags'
import { useQueryLoginUserContext } from 'src/features/tanStackQuery/commonQueries/useQueryLoginUserContext'
import placesOperations from 'src/state/ducks/places/operations'
import usersOperations from 'src/state/ducks/users/operations'
import type { GetUsersFilter } from 'src/state/ducks/users/types'
import type { RootState } from 'src/state/store'
import { adjustCurrentPage } from 'src/util/adjustCurrentPage'
import Loading from '../../components/loading/Loading'
import { useListFilter } from '../../hooks/filter/useListFilter'
import UserList from './UserList'
import UsersFilterDropDown from './UsersFilterDropDown'
import UsersSortDropDown from './UsersSortDropDown'
import EditUser from './editUser/EditUser'
import type { UsersSortOrder } from './usersSortOrder'
import { DEFAULT_USERS_SORT_ORDER, mergeSortIntoFilter } from './usersSortOrder'

const Users: FC = () => {
  const dispatch = useDispatch()

  const { filters, setListFilter } = useListFilter()
  const [userFilters, setUserFilter] = useState<GetUsersFilter>()

  const { canUse: canUseSortAndFilter } = useFeature(
    FeatureNameEnum.USER_LIST_SORT_AND_FILTER,
  )
  const [usersSortOrder, setUsersSortOrder] = useState<UsersSortOrder>(
    DEFAULT_USERS_SORT_ORDER,
  )
  // Feature Flag が OFF のときは並び替えを送らず、API に ORDER BY を付けさせない。
  // 取得中 ('unknown') の間は後述の useEffect が取得自体を保留するため、
  // undefined が実際に使われるのは FF が 'no' に確定したときだけになる。
  const appliedSortOrder =
    canUseSortAndFilter === 'yes' ? usersSortOrder : undefined
  // 同じ Feature Flag で、初期表示時にログインユーザーの所属現場へ暗黙で絞り込むのをやめる。
  // 判定中は undefined を渡し、UsersFilterDropDown 側で絞り込みの確定を待たせる
  const canUseNewPlaceFilter =
    canUseSortAndFilter === 'unknown'
      ? undefined
      : canUseSortAndFilter === 'yes'

  const { data: loginUserContext } = useQueryLoginUserContext()
  const linkToKaminashiIDUserListPage = new URL(
    `/realms/${loginUserContext?.rumpRealmId ?? ''}/members`,
    getKaminashiIDBasePath(),
  ).toString()

  const {
    places: { places },
    placeGroups: { nodes: placeGroups = [] },
    placeNodes: { isLoading: isLoadingPlaceNode },
  } = useSelector((state: RootState) => state.placesState)

  const setPlaces = places ? [...places] : []
  const placeNodes = getUniqueFlattenNodes([
    ...flattenNodes(placeGroups),
    ...setPlaces,
  ])
  const {
    users: {
      users,
      isShowEditUserDrawer,
      updatedUsers,
      count,
      request,
      filter,
      isLoading: isLoadingUser,
    },
    userGroups: { isLoading: isLoadingUserGroup },
  } = useSelector((state: RootState) => state.usersState)

  const DEFAULT_PAGE_SIZE = 25
  const rowsPerPage = request.limit ?? DEFAULT_PAGE_SIZE

  const isLoading = isLoadingUser || isLoadingUserGroup || isLoadingPlaceNode

  useEffect(() => {
    // eslint-disable-next-line react-hooks/immutability -- 関数の宣言順序による既存パターンのため、リファクタリング時に修正する
    getPlaces()
    // eslint-disable-next-line react-hooks/immutability -- 関数の宣言順序による既存パターンのため、リファクタリング時に修正する
    getPlaceNodes()
    // eslint-disable-next-line react-hooks/immutability -- 関数の宣言順序による既存パターンのため、リファクタリング時に修正する
    getUserGroups()

    if (filters && filters.users) {
      const usersFilter = filters?.users
      // eslint-disable-next-line react-hooks/immutability -- 関数の宣言順序による既存パターンのため、リファクタリング時に修正する
      updatePagination(
        usersFilter?.pagination?.limit ?? 25,
        usersFilter?.pagination?.offset ?? 0,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setListFilter({
      ...(filters ?? {}),
      users: {
        pagination: request,
        filter: filters?.users?.filter,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request.limit, request.offset])

  // 絞り込みの更新、および Feature Flag の解決を検知して再取得する。
  // 並び替えの変更は 1 ページ目に戻す必要があるため handleSortChange 側で取得している。
  useEffect(() => {
    // UsersFilterDropDown の useEffect 側で setUserFilter が必ず設定されるので undefined のときはスキップする
    if (!userFilters) {
      return
    }
    // Feature Flag が判定中に取得すると、'yes' に解決した直後に並び替え付きで取り直すことになり
    // 一覧が二度読み込まれるため、判定が確定するまで待つ
    if (canUseSortAndFilter === 'unknown') {
      return
    }
    // eslint-disable-next-line react-hooks/immutability -- 関数の宣言順序による既存パターンのため、リファクタリング時に修正する
    getUsers(request, mergeSortIntoFilter(userFilters, appliedSortOrder))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFilters, canUseSortAndFilter])

  // ユーザー更新があった際に再更新
  useEffect(() => {
    if (!isLoadingUser && updatedUsers) {
      getUsers(request, mergeSortIntoFilter(userFilters, appliedSortOrder))
      // eslint-disable-next-line react-hooks/immutability -- 関数の宣言順序による既存パターンのため、リファクタリング時に修正する
      changeUpdatedUsersFlag(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingUser])

  // 現在のページを調整する
  const currentPage = adjustCurrentPage(request.limit, request.offset, count)

  // 閉じるボタン or Drawer外のクリックしたとき
  const onClose = () => {
    updateSegmentIndex(0)
    changeIsShowEditUserDrawer(false)
  }

  const getUsers = (request: GetUsersRequest, filter?: GetUsersFilter) => {
    usersOperations.getUsers(dispatch, request, filter)
  }
  const changeUsersPage = (
    page: number,
    pageSize?: number,
    filter?: GetUsersFilter,
  ) => {
    usersOperations.changeUsersPage(dispatch, page, pageSize, filter)
  }
  const changeUsersSize = (pageSize: number, filter?: GetUsersFilter) => {
    usersOperations.changeUsersSize(dispatch, pageSize, filter)
  }
  const updatePagination = (limit: number, offset: number) => {
    usersOperations.updatePagination(dispatch, limit, offset)
  }
  const getUserGroups = () => {
    usersOperations.getUserGroups(dispatch, {})
  }
  const getPlaces = () => {
    placesOperations.getPlaces(dispatch, {})
    placesOperations.getPlaceGroups(dispatch)
  }
  const getPlaceNodes = () => {
    placesOperations.getPlaceNodes(dispatch)
  }
  const updateSegmentIndex = (index: number) => {
    usersOperations.updateSegmentIndex(dispatch, index)
  }
  const changeIsShowEditUserDrawer = (isShow: boolean) => {
    usersOperations.changeIsShowEditUserDrawer(dispatch, isShow)
  }
  const changeUpdatedUsersFlag = (edited: boolean) => {
    usersOperations.changeUpdatedUsersFlag(dispatch, edited)
  }

  const handleSortChange = (
    isClear: boolean,
    sortColumn: UserSortColumnEnum | undefined,
    sortOrder: SortOrderEnum,
  ) => {
    const isCleared = isClear || !sortColumn
    const nextSortOrder: UsersSortOrder = isCleared
      ? DEFAULT_USERS_SORT_ORDER
      : { sortColumn, sortOrder }

    setUsersSortOrder(nextSortOrder)

    // 並び順が変わると各ページに並ぶユーザーが変わるため、1 ページ目に戻して取得し直す。
    // changeUsersPage は saga の監視対象であり、これ自体が再取得の起点になる
    changeUsersPage(
      1,
      rowsPerPage,
      mergeSortIntoFilter(userFilters, nextSortOrder),
    )
  }

  const renderUserList = () => {
    return (
      <Stack sx={{ width: '100%' }}>
        <Alert
          variant="outlined"
          severity="warning"
          icon={<InfoOutline />}
          sx={{
            // 例外的に背景色を使用
            backgroundColor: colors.yellow[0],
            m: 2,
          }}
          action={
            <Button
              color="primary"
              component="a"
              variant="contained"
              size="medium"
              endIcon={<OpenInNew />}
              href="https://kaminashi.svy.ooo/ng/answers/d0365de9f3b210f1a43b8153b5d932/"
              target="_blank"
              sx={{
                mt: '-2px',
              }}
            >
              見積もりを相談する
            </Button>
          }
        >
          <Typography variant="body1" fontWeight={'bold'}>
            右のボタンからユーザー追加の相談が可能です。
          </Typography>
        </Alert>

        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 2, pb: 2 }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <UsersFilterDropDown
              places={places}
              placeNodes={placeNodes}
              updatedUsers={updatedUsers}
              isLoadingUser={isLoadingUser}
              request={request}
              filters={filters}
              setUserFilter={setUserFilter}
              canUseNewPlaceFilter={canUseNewPlaceFilter}
              resetPagination={() => updatePagination(rowsPerPage, 0)}
            />
            {canUseSortAndFilter === 'yes' && (
              <UsersSortDropDown
                getSortedUsers={handleSortChange}
                usersSortOrder={usersSortOrder}
              />
            )}
          </Stack>
          <div>
            {loginUserContext?.isRumpRealmAdmin && (
              // カミナシ ID管理のユーザー一覧ページへのリンクボタン
              <Button
                color="primary"
                component="a"
                variant="contained"
                size="medium"
                endIcon={<OpenInNew />}
                href={linkToKaminashiIDUserListPage}
                target="_blank"
              >
                ユーザーを追加
              </Button>
            )}
          </div>
        </Stack>

        {!isLoading && <UserList users={users} />}
        {isLoading && <Loading />}
      </Stack>
    )
  }

  return (
    <Stack>
      <Box
        sx={{
          height: 72,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Header>
          <HeaderTitle title="ユーザー" />
        </Header>
      </Box>
      {renderUserList()}
      <TablePagination
        component="div"
        count={count}
        // TablePagination の page は 0 始まりのため、currentPage から 1 を引く
        page={currentPage - 1}
        onPageChange={(_, newPage) =>
          changeUsersPage(newPage + 1, rowsPerPage, filter)
        }
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={event => {
          const size = parseInt(event.target.value, 10)
          changeUsersSize(size, filter)
        }}
        rowsPerPageOptions={[25, 50, 100]}
        labelRowsPerPage="1ページあたり:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}〜${to} (${count !== -1 ? count : `不明`} 件中)`
        }
        sx={{
          '& .MuiTablePagination-selectLabel': {
            margin: 0,
          },
          '& .MuiTablePagination-displayedRows': {
            margin: 0,
          },
        }}
      />
      <Drawer
        anchor="right"
        open={isShowEditUserDrawer}
        onClose={() => onClose()}
        hideBackdrop={false}
        ModalProps={{
          keepMounted: false, // destroyOnClose={true} に相当
        }}
        slotProps={{
          paper: {
            sx: {
              maxHeight: '100%',
              width: '100%',
              maxWidth: 600,
            },
          },
        }}
      >
        <EditUser />
      </Drawer>
    </Stack>
  )
}

export default withRouter(Users)
